import * as React from 'react';
import { observer } from 'mobx-react';
import {
  AiOutlineCheck, AiOutlineClose, AiOutlineCopy, AiOutlineFileExcel,
  AiOutlineFilePdf, AiOutlineFilePpt, AiOutlineFileWord, AiOutlineHistory,
  AiOutlineLike, AiOutlineDislike, AiOutlinePaperClip, AiOutlineArrowUp,
  AiOutlineArrowDown, AiOutlineForm, AiOutlineExclamationCircle,
} from 'react-icons/ai';
import { BsStopFill } from 'react-icons/bs';
import AgentStore, { Message } from '../../../stores/AgentStore';
import { CheckBox } from '../../forms';
import { AgentProgress, AgentStepLine, progressText } from './types';
import { Block, blocks, segments, splitFooter } from './markdown';
import { requestedFormats } from '../../../../agent/runtime/files';
import {
  toExcel, toPdf, toSlides, toWord,
} from './download';
import style from './AgentPanel.module.scss';
import { HistoryList } from './HistoryList';

interface AgentPanelProps { agentStore: AgentStore; }

interface AgentPanelState {
  width: number;
  question: string;
  copied: number;
  hasRuntime: boolean;
  /** 대화 목록이 바닥에서 떨어져 있는가. 떨어져 있으면 「맨 아래로」 단추를 띄운다. */
  awayFromBottom: boolean;
}

const WIDTH_KEY = 'elman-agent-panel-width';
const MIN_WIDTH = 320;
const MAX_WIDTH = 640;
const DEFAULT_WIDTH = 380;
/** 입력창이 늘어나는 한도. 이보다 길면 입력창 안에서 스크롤한다. */
const COMPOSER_MAX_HEIGHT = 168;
// 시나리오로 끝까지 돌려 본 것만 남긴다. 첫 화면의 예시가 실패하면 그 다음은 없다.
//
// 셋이 서로 다른 일을 보여 준다 — 초안 작성, 조회, 파일 만들기. 앞의 둘은 되묻기 없이
// 한 번에 끝나고, 마지막은 실제로 .docx 가 나온다.
const HINTS = [
  '내일 하루 연차 쓸게. 늘 쓰던 대로 신청서 올려줘',
  '이번 달 발주 현황 알려줘',
  '월간 인사회의용 이번 달 입퇴사·인원 현황 워드 문서로 만들어줘',
];
const RUNTIME_POLL_MS = 500;
// eslint-disable-next-line no-underscore-dangle
const runtimeReady = (): boolean => Boolean(window.__elmanAgentAsk);
const RUNTIME_POLL_LIMIT = 20;

/** `3.2초`. */
function seconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}초`;
}


/** 이어진 목록 줄을 하나의 목록으로 묶는다. 줄마다 따로 그리면 글머리표 간격이 들쭉날쭉하다. */
type Group = Block | { type: 'items'; items: string[] };

function grouped(source: string): Group[] {
  const result: Group[] = [];
  blocks(source).forEach((block) => {
    const last = result[result.length - 1];
    if (block.type === 'list' && last?.type === 'items') last.items.push(block.text);
    else if (block.type === 'list') result.push({ type: 'items', items: [block.text] });
    else result.push(block);
  });
  return result;
}

@observer
export class AgentPanel extends React.Component<AgentPanelProps, AgentPanelState> {
  state: AgentPanelState = {
    width: Number(localStorage.getItem(WIDTH_KEY)) || DEFAULT_WIDTH,
    question: '',
    copied: -1,
    hasRuntime: runtimeReady(),
    awayFromBottom: false,
  };

  private copiedTimer = 0;

  private runtimeTimer = 0;

  private runtimeTries = 0;

  private dragFrom = 0;

  private dragWidth = DEFAULT_WIDTH;

  private messagesRef = React.createRef<HTMLDivElement>();

  private inputRef = React.createRef<HTMLTextAreaElement>();

  /** 파일 고르기 창은 숨은 `input` 을 눌러 연다. 브라우저가 다른 길을 안 준다. */
  private picker = React.createRef<HTMLInputElement>();

  /** 갱신 직전에 바닥에 있었는가. 위로 올려 읽는 중이면 끌어내리지 않는다. */
  private wasAtBottom = true;

  componentDidMount() {
    if (!this.state.hasRuntime) this.runtimeTimer = window.setInterval(this.lookForRuntime, RUNTIME_POLL_MS);
    this.scrollToBottom();
    this.fitInput();
  }

  /**
   * 답이 길어지면 새 글이 화면 밖에 쌓인다. 사람이 매번 손으로 내려야 했다.
   *
   * 다만 무조건 내리면 위로 올려 읽던 중에 끌려 내려간다 — 답변이 흘러 들어오는
   * 동안 그러면 읽을 수가 없다. **갱신 직전에 바닥에 있었을 때만** 따라간다.
   */
  getSnapshotBeforeUpdate(): null {
    const list = this.messagesRef.current;
    this.wasAtBottom = !list
      || list.scrollHeight - list.scrollTop - list.clientHeight < 40;
    return null;
  }

  componentDidUpdate(_props: AgentPanelProps, before: AgentPanelState) {
    if (this.wasAtBottom) this.scrollToBottom();
    if (before.question !== this.state.question) this.fitInput();
  }

  componentWillUnmount() {
    this.props.agentStore.cancel();
    window.clearTimeout(this.copiedTimer);
    window.clearInterval(this.runtimeTimer);
    this.stopResize();
  }

  private scrollToBottom(smooth = false): void {
    const list = this.messagesRef.current;
    if (!list) return;
    if (smooth && list.scrollTo) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
    else list.scrollTop = list.scrollHeight;
  }

  private onScroll = () => {
    const list = this.messagesRef.current;
    if (!list) return;
    const away = list.scrollHeight - list.scrollTop - list.clientHeight > 120;
    if (away !== this.state.awayFromBottom) this.setState({ awayFromBottom: away });
  };

  /** 입력창을 글 높이만큼 늘인다. 줄을 지우면 다시 줄어든다. */
  private fitInput(): void {
    const input = this.inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
    input.style.overflowY = input.scrollHeight > COMPOSER_MAX_HEIGHT ? 'auto' : 'hidden';
  }

  private lookForRuntime = () => {
    this.runtimeTries += 1;
    if (runtimeReady()) {
      window.clearInterval(this.runtimeTimer);
      this.setState({ hasRuntime: true });
    } else if (this.runtimeTries >= RUNTIME_POLL_LIMIT) window.clearInterval(this.runtimeTimer);
  };

  private startResize = (event: React.MouseEvent) => {
    this.dragFrom = event.clientX;
    this.dragWidth = this.state.width;
    document.addEventListener('mousemove', this.resize);
    document.addEventListener('mouseup', this.stopResize);
  };

  private resize = (event: MouseEvent) => {
    const width = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, this.dragWidth + this.dragFrom - event.clientX));
    this.setState({ width });
    localStorage.setItem(WIDTH_KEY, String(width));
  };

  private stopResize = () => {
    document.removeEventListener('mousemove', this.resize);
    document.removeEventListener('mouseup', this.stopResize);
  };

  private send = async () => {
    const question = this.state.question.trim();
    // **`busy` 로 막지 않는다.** 예전에는 여기서 그냥 `return` 했다 — 질문은 줄에 서지도
    // 않고 말도 없이 사라졌다. 지금은 스토어가 줄을 세우고 앞 작업이 끝나면 이어서 한다.
    if (!question || !runtimeReady()) return;
    this.setState({ question: '' });
    // 보낸 사람은 답을 보려 한다. 위로 올려 읽던 중이었어도 바닥으로 데려간다.
    this.wasAtBottom = true;
    this.scrollToBottom();
    await this.props.agentStore.ask(question);
  };

  /**
   * Enter 는 보내기, Shift+Enter 는 줄바꿈이다.
   *
   * 한글은 조합 중에 Enter 가 한 번 더 온다(`isComposing`·keyCode 229). 그것을 보내기로
   * 받으면 마지막 글자가 두 번 가거나 반쪽 질문이 나간다.
   */
  private onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    this.send();
  };

  private copy = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    this.setState({ copied: index });
    window.clearTimeout(this.copiedTimer);
    this.copiedTimer = window.setTimeout(() => this.setState({ copied: -1 }), 2000);
  };

  /**
   * 답 본문과 끝의 근거(조회한 화면·조회 중 알림·빠진 항목 안내)를 나눠 그린다. 근거는 접어 둔다 —
   * 사람은 본문을 읽고, 어디서 봤는지는 궁금할 때 펼친다(2026-10-22). 다 쓴 답에서만 접는다.
   */
  private renderBody(text: string, done: boolean) {
    const { body, footer } = done ? splitFooter(text) : { body: text, footer: [] as string[] };
    return (
      <>
        {this.renderAnswer(body)}
        {footer.length > 0 && (
          <details className={style.footer}>
            <summary>근거 보기</summary>
            {footer.map((line, index) => <p key={index}>{line}</p>)}
          </details>
        )}
      </>
    );
  }

  private renderAnswer(text: string) {
    const inline = (value: string) => segments(value).map((part, index) => (
      part.bold ? <strong key={index}>{part.text}</strong> : <React.Fragment key={index}>{part.text}</React.Fragment>
    ));
    return grouped(text).map((block, index) => {
      if (block.type === 'table') {
        const [head, ...rest] = block.rows;
        // 좁은 패널에서 표가 칸마다 줄을 꺾으면 날짜·금액이 두 줄로 갈라진다.
        // 줄은 지키고 표만 옆으로 밀어 보게 한다.
        return (
          <div key={index} className={style.tableWrap}>
            <table>
              <thead><tr>{head.map((cell, i) => <th key={i}>{inline(cell)}</th>)}</tr></thead>
              <tbody>
                {rest.map((row, r) => (
                  <tr key={r}>{row.map((cell, c) => <td key={c}>{inline(cell)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      if (block.type === 'items') {
        return <ul key={index}>{block.items.map((item, i) => <li key={i}>{inline(item)}</li>)}</ul>;
      }
      if (block.type === 'code') return <pre key={index}>{block.text}</pre>;
      if (block.type === 'heading') return <h3 key={index}>{inline(block.text)}</h3>;
      return <p key={index}>{inline(block.text)}</p>;
    });
  }

  /**
   * 답변이 끝난 뒤에만 보인다. 흐르는 도중에 누르면 잘린 것을 받는다.
   *
   * 파일 버튼은 **그 답변에 표가 있고** 사용자가 그 형식을 말했을 때만 나온다.
   */
  private renderActions(message: Message, index: number, question: string) {
    if (message.progress?.kind !== 'done' || !message.text) return null;
    const { table } = message;
    // 작성 부서는 받는 순간 로그인한 사람으로 — 보고 문서 제목 아래 「(기준일) · 부서 이름」.
    const author = this.props.agentStore.reportAuthor;
    const report = message.report && (author ? { ...message.report, author } : message.report);
    const asked = message.formats?.length ? message.formats : requestedFormats(question);
    const files = [
      {
        format: 'xlsx' as const,
        label: '엑셀로 받기',
        icon: <AiOutlineFileExcel />,
        save: () => toExcel(table!, question, report),
      },
      {
        format: 'docx' as const,
        label: '문서로 받기',
        icon: <AiOutlineFileWord />,
        save: () => toWord(table, message.text, question, report),
      },
      {
        format: 'pdf' as const,
        label: 'PDF로 인쇄',
        icon: <AiOutlineFilePdf />,
        save: () => toPdf(table, message.text, question, report),
      },
      {
        format: 'pptx' as const,
        label: '발표자료로 받기',
        icon: <AiOutlineFilePpt />,
        save: () => toSlides(table, message.text, question, report),
      },
    ];
    const copied = this.state.copied === index;

    return (
      <div className={style.toolbar}>
        {/* 서버에 맡긴 일이 끝났다는 알림. 누르면 앱 대기열의 「실행」과 같은 일을 한다. */}
        {message.finished && (
          <button
            type="button"
            className={style.fileButton}
            disabled={!this.props.agentStore.canRunFinished(message.finished)}
            title={this.props.agentStore.canRunFinished(message.finished)
              ? undefined : '대기열에서 이미 실행했거나 지웠습니다'}
            onClick={() => this.props.agentStore.runFinished(message.finished!)}
          >
            결과 받기
          </button>
        )}
        {files.filter((file) => asked.includes(file.format)
          && (file.format !== 'xlsx' || Boolean(table))).map((file) => (
          <button key={file.format} type="button" className={style.fileButton} onClick={file.save}>
            {file.icon}
            {file.label}
          </button>
        ))}
        <button
          type="button"
          className={style.iconButton}
          title={copied ? '복사했습니다' : '답변 복사'}
          aria-label="답변 복사"
          onClick={() => this.copy(message.text, index)}
        >
          {copied ? <AiOutlineCheck /> : <AiOutlineCopy />}
        </button>
        {message.experience && (
          <>
            <button
              type="button"
              className={message.feedback === 'positive' ? `${style.rateButton} ${style.rated}` : style.rateButton}
              title="이 답이 맞으면 눌러 주세요. 다음에 같은 방식으로 답합니다."
              onClick={() => this.props.agentStore.rate(index, 'positive')}
            >
              {message.feedback === 'positive' ? <AiOutlineCheck /> : <AiOutlineLike />}
              맞아요
            </button>
            <button
              type="button"
              className={message.feedback === 'negative' ? `${style.rateButton} ${style.rated}` : style.rateButton}
              title="이 답이 틀리면 눌러 주세요."
              onClick={() => this.props.agentStore.rate(index, 'negative')}
            >
              {message.feedback === 'negative' ? <AiOutlineCheck /> : <AiOutlineDislike />}
              틀려요
            </button>
          </>
        )}
      </div>
    );
  }

  /**
   * 되묻기의 선택지(`hints`)와 답 아래 선택 단추(`choices`, 「숫자 붙은 계정까지 넣어서 다시 보여줘」). 벤치가 되묻기 단추를
   * `[class*=hints] button`으로 찾아 누른다. 그 이름과 모양(단추)을 지킨다. 답 아래 단추에 같은 이름을 쓰면 벤치가 그것을
   * 되묻기 답으로 눌렀다(독립 검토).
   */
  private renderOptions(message: Message) {
    const { progress } = message;
    const options = progress?.kind === 'ask' || progress?.kind === 'done' ? progress.options : undefined;
    if (!options?.length) return null;
    return (
      <div className={progress?.kind === 'ask' ? style.hints : style.choices}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => this.props.agentStore.ask(option)}
          >
            {option}
          </button>
        ))}
      </div>
    );
  }

  /**
   * 붙인 파일을 보여 준다. **무엇이 함께 가는지 보이지 않으면 사람은 확인할 수 없다.**
   *
   * 못 받은 이유도 같은 자리에 낸다 — 파일을 고른 자리에서 답이 나와야 다시 고른다.
   */
  private renderAttached() {
    const { attached, attachTrouble } = this.props.agentStore;
    if (!attached.length && !attachTrouble) return null;
    return (
      <div className={style.attached}>
        {attached.map((file, index) => (
          <span key={`${file.name}-${index}`} className={style.chip}>
            <AiOutlinePaperClip />
            <span className={style.chipName}>{file.name}</span>
            <button
              type="button"
              aria-label={`${file.name} 빼기`}
              title="빼기"
              onClick={() => this.props.agentStore.detach(index)}
            >
              <AiOutlineClose />
            </button>
          </span>
        ))}
        {attachTrouble && <small className={style.trouble}>{attachTrouble}</small>}
      </div>
    );
  }

  private renderComposer(docked: boolean) {
    const { question, hasRuntime, awayFromBottom } = this.state;
    const { busy, queued } = this.props.agentStore;
    const canSend = hasRuntime && Boolean(question.trim());
    return <div className={docked ? `${style.dock} ${style.docked}` : style.dock}>
      {/* 글 위에 떠 있으면 답을 가린다. 입력창 위에 제 자리를 따로 두고 그 안에 선다. */}
      {docked && awayFromBottom && (
        <div className={style.jumpRow}>
          <button
            type="button"
            className={style.jump}
            title="맨 아래로"
            aria-label="맨 아래로"
            onClick={() => this.scrollToBottom(true)}
          >
            <AiOutlineArrowDown />
          </button>
        </div>
      )}
      {/* **기다리는 질문을 보여 준다.** 안 보여 주면 줄에 섰는지 사라졌는지 알 수 없다. */}
      {queued.map((waiting, at) => (
        <div key={`${at}-${waiting}`} className={style.queued}>
          <span>차례를 기다립니다 · {waiting}</span>
          <button type="button" title="빼기" aria-label="기다리는 질문 빼기" onClick={() => this.props.agentStore.unqueue(at)}>
            <AiOutlineClose />
          </button>
        </div>
      ))}
      <div className={style.composer}>
        {this.renderAttached()}
        <textarea
          ref={this.inputRef}
          rows={1}
          value={question}
          // **`busy` 로 잠그지 않는다.** 한 턴이 몇 분 걸리는데 그 동안 아무것도 못 쳤다.
          readOnly={!hasRuntime}
          placeholder={(() => {
            if (!hasRuntime) return 'AI 준비 중';
            return busy ? '지금 하는 일이 끝나면 이어서 합니다' : '무엇이든 물어보세요';
          })()}
          onChange={(event) => this.setState({ question: event.target.value })}
          onKeyDown={this.onKeyDown}
        />
        <div className={style.composerBar}>
          {/* 읽을 수 있는 것만 고르게 한다. `accept` 는 권고일 뿐이라 스토어가 다시 본다. */}
          <input
            ref={this.picker}
            type="file"
            multiple
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              // 같은 파일을 두 번 고를 수 있게 값을 비운다. 안 그러면 두 번째가 안 먹는다.
              if (this.picker.current) this.picker.current.value = '';
              if (chosen.length) this.props.agentStore.attach(chosen);
            }}
          />
          <button
            type="button"
            className={style.iconButton}
            title="문서·사진 붙이기"
            aria-label="문서·사진 붙이기"
            disabled={!hasRuntime || busy}
            onClick={() => this.picker.current?.click()}
          >
            <AiOutlinePaperClip />
          </button>
          <span className={style.spacer} />
          {/* **멈춤과 보내기가 같이 있어야 한다.** 하나만 두면, 줄을 세우려고 치는 동안
              멈출 길이 사라지거나 그 반대가 된다. */}
          {busy && (
            <button
              type="button"
              className={style.stopButton}
              title="멈추기"
              aria-label="멈추기"
              onClick={() => this.props.agentStore.cancel()}
            >
              <BsStopFill />
            </button>
          )}
          <button
            type="button"
            className={style.sendButton}
            title="보내기 (Enter) · 줄바꿈은 Shift+Enter"
            aria-label="보내기"
            // 파일만 붙이고 보내면 무엇을 하라는 것인지 모른다. 말은 언제나 있어야 한다.
            disabled={!canSend}
            onClick={() => this.send()}
          >
            <AiOutlineArrowUp />
          </button>
        </div>
      </div>
    </div>;
  }

  private renderStatus(progress: AgentProgress) {
    if (progress.kind === 'error') {
      return (
        <div className={style.failure} role="alert">
          <AiOutlineExclamationCircle />
          <span>{progress.message}</span>
        </div>
      );
    }
    // 답을 흘리는 동안은 그 글이 진행 표시다.
    const text = progress.kind === 'answering' ? '' : progressText(progress);
    if (!text) return null;
    return (
      <div className={style.status}>
        <span className={style.pulse} />
        <span className={style.shimmer}>{text}</span>
      </div>
    );
  }

  /** 끝난 단계들 — `✓ 요청 파악 3.2초`. 일하는 동안만 보인다. */
  private renderSteps(steps: AgentStepLine[] | undefined, progress: AgentProgress | undefined) {
    const working = progress && !['done', 'ask', 'error'].includes(progress.kind);
    const past = (steps ?? []).filter((one) => one.ms !== undefined);
    if (!working || !past.length) return null;
    return (
      <ul className={style.steps}>
        {past.map((one) => (
          <li key={`${one.key}-${one.at}`}>
            <AiOutlineCheck />
            <span>{one.past}</span>
            <span className={style.stepTime}>{seconds(one.ms as number)}</span>
          </li>
        ))}
      </ul>
    );
  }

  private renderThread(messages: Message[]) {
    return messages.map((message, index) => {
      if (message.role === 'user') {
        return (
          <div key={index} className={style.user}>
            <div className={style.bubble}><p>{message.text}</p></div>
            <div className={style.userTools}>
              <button
                type="button"
                className={style.iconButton}
                title={this.state.copied === index ? '복사했습니다' : '질문 복사'}
                aria-label="질문 복사"
                onClick={() => this.copy(message.text, index)}
              >
                {this.state.copied === index ? <AiOutlineCheck /> : <AiOutlineCopy />}
              </button>
            </div>
          </div>
        );
      }
      return (
        <div key={index} className={style.assistant}>
          <div className={style.answer}>
            {this.renderSteps(message.steps, message.progress)}
            {message.progress && this.renderStatus(message.progress)}
            {message.text && this.renderBody(message.text, message.progress?.kind === 'done')}
            {message.progress?.kind === 'answering' && <span className={style.cursor} />}
          </div>
          {/* 그 답변을 부른 질문은 **바로 앞** 사용자 메시지다. 마지막 질문을 쓰면
              지난 답변의 버튼이 나중 질문으로 파일 이름을 짓고 형식도 그것으로 정한다. */}
          {this.renderActions(message, index, messages[index - 1]?.text ?? '')}
          {this.renderOptions(message)}
        </div>
      );
    });
  }

  private renderWelcome(hasRuntime: boolean) {
    return (
      <div className={style.welcome}>
        <p className={style.greeting}>오늘은 무엇이 궁금하세요?</p>
        {!hasRuntime && <div className={style.notice}>AI가 아직 준비되지 않았습니다.</div>}
        {this.renderComposer(false)}
        <div className={style.suggestions}>
          {HINTS.map((hint) => (
            <button
              key={hint}
              type="button"
              disabled={!hasRuntime}
              onClick={() => this.setState({ question: hint }, () => { this.send(); })}
            >
              {hint}
            </button>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { width, hasRuntime } = this.state;
    const { agentStore } = this.props;
    const { messages } = agentStore;

    return (
      <aside className={style.panel} style={{ '--panel-width': `${width}px` } as React.CSSProperties}>
        <div className={style.resize} onMouseDown={this.startResize} />

        <header>
          <strong>{agentStore.showingHistory ? '대화 기록' : 'AI 도우미'}</strong>
          <div className={style.headerButtons}>
            <label className={style.auto} title="켜면 저장·삭제와 메일·문자·팩스·카카오톡 전송까지 대신합니다. 로그아웃 전까지 유지됩니다.">
              <CheckBox
                value={agentStore.auto}
                onChange={(value) => agentStore.setAuto(value)}
                noMargin={true}
              />
              <span>Auto</span>
            </label>
            <button
              type="button"
              title="새 대화"
              // 하는 중에 새 대화로 옮기면 흐르던 답이 새 대화에 써진다.
              disabled={agentStore.busy || (!agentStore.showingHistory && messages.length === 0)}
              onClick={() => agentStore.newThread()}
            >
              <AiOutlineForm />
            </button>
            <button
              type="button"
              title="대화 기록"
              className={agentStore.showingHistory ? style.on : undefined}
              onClick={() => { agentStore.showingHistory = !agentStore.showingHistory; }}
            >
              <AiOutlineHistory />
            </button>
            <button type="button" title="닫기" onClick={() => agentStore.toggle()}>
              <AiOutlineClose />
            </button>
          </div>
        </header>

        {agentStore.showingHistory && <HistoryList agentStore={agentStore} />}
        {!agentStore.showingHistory && messages.length === 0 && this.renderWelcome(hasRuntime)}
        {!agentStore.showingHistory && messages.length > 0 && (
          <>
            <div className={style.messages} ref={this.messagesRef} onScroll={this.onScroll}>
              {!hasRuntime && <div className={style.notice}>AI가 아직 준비되지 않았습니다.</div>}
              {this.renderThread(messages)}
            </div>
            {this.renderComposer(true)}
          </>
        )}
      </aside>
    );
  }
}
